import React from "react";
import { Link, useOutletContext } from "react-router-dom";
import * as supplierService from "../../services/supplierService";
import Invitations from "./Invitations";
import { EmptyState, LoadState, PageHeader, cardStyle, pageStyle, useLoad } from "../../components/ui";

function Tile({ label, value, to, tone = "#111827", note }) {
  return (
    <Link to={to} style={{ ...cardStyle, padding: "14px 16px", textDecoration: "none", display: "block" }}>
      <div style={{ fontSize: 12, color: "#6B7280" }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 700, color: tone, lineHeight: 1.2 }}>{value}</div>
      {note ? <div style={{ fontSize: 12, color: "#6B7280" }}>{note}</div> : null}
    </Link>
  );
}

// The supplier's landing page: who they work for, what is missing, and any invitation waiting.
export default function SupplierHome() {
  const { me } = useOutletContext() || {};
  const buyers = useLoad(() => supplierService.getBuyers(), []);
  const docs = useLoad(() => supplierService.getDocuments(), []);
  const dash = useLoad(() => supplierService.getDashboard(), []);
  const list = buyers.data || [];
  const types = (docs.data && docs.data.types) || [];
  const uploaded = new Set(((docs.data && docs.data.documents) || []).filter((d) => !d.expired).map((d) => d.document_type_code));
  const missing = types.filter((t) => t.is_mandatory && !uploaded.has(t.code)).length;
  const expired = ((docs.data && docs.data.documents) || []).filter((d) => d.expired).length;

  return (
    <div style={pageStyle}>
      <PageHeader title={me ? `Welcome, ${me.user.first_name}` : "Welcome"} subtitle={me && me.company ? me.company.name : "Your supplier portal"} />
      <Invitations canAccept={!!(me && me.is_primary_user)} onAccepted={() => buyers.reload()} />
      <LoadState loading={buyers.loading || docs.loading} error={buyers.error || docs.error} onRetry={() => { buyers.reload(); docs.reload(); }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 12 }} className="mb-4">
          <Tile label="RFQs open for you" value={(dash.data && dash.data.rfqs.open) || 0} to="/supplier/rfqs" tone={dash.data && dash.data.rfqs.open ? "#065F46" : "#111827"} note={dash.data && dash.data.rfqs.open ? "Send your quotation" : "Nothing waiting"} />
          <Tile label="Orders waiting for your answer" value={(dash.data && dash.data.orders.waiting_for_answer) || 0} to="/supplier/orders" tone={dash.data && dash.data.orders.waiting_for_answer ? "#92400E" : "#111827"} note={dash.data && dash.data.orders.in_progress ? `${dash.data.orders.in_progress} on their way` : undefined} />
          <Tile label="Unread messages" value={(dash.data && dash.data.unread_messages) || 0} to="/supplier/messages" tone={dash.data && dash.data.unread_messages ? "#92400E" : "#111827"} />
          <Tile label="Buyers you work for" value={list.length} to="/supplier/buyers" />
          <Tile label="Documents to upload" value={missing} to="/supplier/company?tab=documents" tone={missing ? "#92400E" : "#065F46"} note={missing ? "Required by your buyers" : "Nothing missing"} />
          <Tile label="Documents expired" value={expired} to="/supplier/company?tab=documents" tone={expired ? "#991B1B" : "#065F46"} note={expired ? "Upload a new copy" : "All in date"} />
        </div>
        {list.length === 0 ? (
          <EmptyState title="No buyers yet" text="When a buyer invites you, accept the invitation here or from the link in the email. Their requests for quotation will appear in this portal." />
        ) : (
          <div style={{ ...cardStyle, padding: 16, fontSize: 14, color: "#374151" }}>
            Requests for quotation from your buyers appear under RFQs. You also get a notification when one arrives.
          </div>
        )}
      </LoadState>
    </div>
  );
}
